/**
 * ODDIT Header
 * Handles mega menu toggling (multiple panels), mobile menu, and overlay interactions
 */
(function () {
  'use strict';

  // Shared body-scroll lock (reference-counted). Both header and cart drawer
  // register/unregister named locks so neither component blindly unlocks scroll
  // while the other still needs it.
  window.OdditScrollLock =
    window.OdditScrollLock ||
    (function () {
      var locks = new Set();
      return {
        lock: function (id) {
          if (locks.size === 0) {
            var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            document.body.style.paddingRight = scrollbarWidth + 'px';
          }
          locks.add(id);
        },
        unlock: function (id) {
          locks.delete(id);
          if (locks.size === 0) {
            document.documentElement.style.overflow = '';
            document.body.style.paddingRight = '';
          }
        },
        isLocked: function () {
          return locks.size > 0;
        },
      };
    })();

  const SELECTORS = {
    header: '.oddit-header',
    nav: '.oddit-header__nav',
    menuToggle: '[data-mobile-menu-toggle]',
    megaMenuTrigger: '[data-mega-menu-trigger]',
    megaMenuPanel: '[data-mega-menu-panel]',
    mobileMenuPanel: '[data-mobile-menu-panel]',
    overlay: '[data-mega-menu-overlay]',
    cartToggle: '[data-cart-toggle]',
  };

  const CLASSES = {
    menuOpen: 'is-open',
    overlayVisible: 'is-visible',
  };

  const BREAKPOINT_DESKTOP = 990;

  class OdditHeader {
    constructor(sectionEl) {
      this.header = sectionEl || document.querySelector(SELECTORS.header);
      if (!this.header) return;

      this.nav = this.header.querySelector(SELECTORS.nav);
      this.menuToggle = this.header.querySelector(SELECTORS.menuToggle);
      this.megaMenuTriggers = this.header.querySelectorAll(SELECTORS.megaMenuTrigger);
      this.megaMenuPanels = this.header.querySelectorAll(SELECTORS.megaMenuPanel);
      this.mobileMenuPanel = this.header.querySelector(SELECTORS.mobileMenuPanel);
      this.overlay = this.header.querySelector(SELECTORS.overlay);
      this.cartToggle = this.header.querySelector(SELECTORS.cartToggle);

      this.isOpen = false;
      this.activeTrigger = null;
      this.activePanel = null;
      this.isDesktop = window.innerWidth >= BREAKPOINT_DESKTOP;

      this._onKeydown = this._onKeydown.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onTriggerMouseEnter = this._onTriggerMouseEnter.bind(this);
      this._onTriggerMouseLeave = this._onTriggerMouseLeave.bind(this);
      this._onPanelMouseEnter = this._onPanelMouseEnter.bind(this);
      this._onPanelMouseLeave = this._onPanelMouseLeave.bind(this);
      this._onHeaderMouseLeave = this._onHeaderMouseLeave.bind(this);
      this._onOverlayMouseEnter = this._onOverlayMouseEnter.bind(this);
      this._resizeTimer = null;
      this._hoverCloseTimer = null;
      this._cartDrawerObserver = null;

      this.init();
    }

    init() {
      this.bindEvents();
      this.handleResize();
    }

    bindEvents() {
      // Desktop: mega menu trigger links toggle their associated panel (click + hover)
      if (this.megaMenuTriggers.length > 0) {
        this.megaMenuTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            var panelId = trigger.getAttribute('data-mega-menu-trigger');
            var panel = this.header.querySelector('[data-mega-menu-panel="' + panelId + '"]');

            if (this.isOpen && this.activePanel === panel) {
              this.closeAll();
            } else {
              this.openDesktopPanel(trigger, panel);
            }
          });
          trigger.addEventListener('mouseenter', this._onTriggerMouseEnter);
          trigger.addEventListener('mouseleave', this._onTriggerMouseLeave);
        });
        this.megaMenuPanels.forEach((panel) => {
          panel.addEventListener('mouseenter', this._onPanelMouseEnter);
          panel.addEventListener('mouseleave', this._onPanelMouseLeave);
        });
        this.header.addEventListener('mouseleave', this._onHeaderMouseLeave);
      }

      // Overlay hover closes menu (overlay is NOT part of keep-open zone; it's "below" the megamenu)
      if (this.overlay) {
        this.overlay.addEventListener('mouseenter', this._onOverlayMouseEnter);
      }

      // Mobile: hamburger toggles mobile menu panel
      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => {
          if (this.isOpen && this.activePanel === this.mobileMenuPanel) {
            this.closeAll();
          } else {
            this.openMobileMenu();
          }
        });
      }

      // Cart toggle opens cart drawer
      if (this.cartToggle) {
        this.cartToggle.addEventListener('click', () => {
          this.toggleCartDrawer();
        });
      }

      // Overlay click closes everything (mouseenter also closes - overlay is not part of megamenu)
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeAll();
        });
      }

      // ESC key closes menu
      document.addEventListener('keydown', this._onKeydown);

      // Window resize handler
      window.addEventListener('resize', this._onResize);

      // Keep cart button aria-expanded in sync when drawer is toggled externally
      this._observeCartDrawer();
      this._onCartUpdated = (e) => {
        this._observeCartDrawer();
        this._updateCartCountBubble(e.detail && e.detail.cart);
      };
      document.addEventListener('cart:updated', this._onCartUpdated);
    }

    _onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        var triggerToRestore = this.activeTrigger;
        this.closeAll();
        if (this.isDesktop && triggerToRestore) {
          triggerToRestore.focus();
        } else if (this.menuToggle) {
          this.menuToggle.focus();
        }
      }
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    }

    _onTriggerMouseEnter(e) {
      if (!this.isDesktop) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
      var trigger = e.currentTarget;
      var panelId = trigger.getAttribute('data-mega-menu-trigger');
      var panel = this.header.querySelector('[data-mega-menu-panel="' + panelId + '"]');
      this.openDesktopPanel(trigger, panel);
    }

    _onTriggerMouseLeave() {
      if (!this.isDesktop || !this.isOpen) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = setTimeout(() => {
        this.closeAll();
        this._hoverCloseTimer = null;
      }, 150);
    }

    _onPanelMouseEnter() {
      if (!this.isDesktop) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    }

    _onPanelMouseLeave() {
      if (!this.isDesktop || !this.isOpen) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = setTimeout(() => {
        this.closeAll();
        this._hoverCloseTimer = null;
      }, 150);
    }

    _onHeaderMouseLeave() {
      if (!this.isDesktop || !this.isOpen) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = setTimeout(() => {
        this.closeAll();
        this._hoverCloseTimer = null;
      }, 150);
    }

    _onOverlayMouseEnter() {
      if (!this.isDesktop || !this.isOpen) return;
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = setTimeout(() => {
        this.closeAll();
        this._hoverCloseTimer = null;
      }, 150);
    }

    handleResize() {
      var wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= BREAKPOINT_DESKTOP;

      if (wasDesktop !== this.isDesktop && this.isOpen) {
        this.closeAll();
      }
    }

    openDesktopPanel(trigger, panel) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
      // Close any currently open panel first
      this._closeAllPanels();

      this.isOpen = true;
      this.activeTrigger = trigger;
      this.activePanel = panel;

      // Open the target panel
      if (panel) {
        panel.classList.add(CLASSES.menuOpen);
        panel.setAttribute('aria-hidden', 'false');
      }

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add(CLASSES.overlayVisible);
        this.overlay.setAttribute('aria-hidden', 'false');
      }

      // Update ARIA on all triggers
      this.megaMenuTriggers.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      window.OdditScrollLock.lock('header-menu');
    }

    openMobileMenu() {
      this._closeAllPanels();

      this.isOpen = true;
      this.activeTrigger = null;
      this.activePanel = this.mobileMenuPanel;

      if (this.mobileMenuPanel) {
        if (this.nav) {
          var navBottom = this.nav.getBoundingClientRect().bottom;
          this.mobileMenuPanel.style.setProperty('--mobile-menu-top', navBottom + 'px');
        }
        this.mobileMenuPanel.classList.add(CLASSES.menuOpen);
        this.mobileMenuPanel.setAttribute('aria-hidden', 'false');
      }

      if (this.overlay) {
        this.overlay.classList.add(CLASSES.overlayVisible);
        this.overlay.setAttribute('aria-hidden', 'false');
      }

      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'true');
        var closeLabel = this.menuToggle.getAttribute('data-close-label') || 'Close menu';
        this.menuToggle.setAttribute('aria-label', closeLabel);
      }

      window.OdditScrollLock.lock('header-menu');
    }

    closeAll() {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
      this.isOpen = false;
      this._closeAllPanels();

      // Hide overlay
      if (this.overlay) {
        this.overlay.classList.remove(CLASSES.overlayVisible);
        this.overlay.setAttribute('aria-hidden', 'true');
      }

      // Reset desktop triggers
      this.megaMenuTriggers.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });

      // Reset mobile toggle
      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'false');
        var openLabel = this.menuToggle.getAttribute('data-open-label') || 'Open menu';
        this.menuToggle.setAttribute('aria-label', openLabel);
      }

      this.activeTrigger = null;
      this.activePanel = null;

      window.OdditScrollLock.unlock('header-menu');
    }

    toggleCartDrawer() {
      if (this.isOpen) {
        this.closeAll();
      }

      if (window.OdditCartDrawer && typeof window.OdditCartDrawer.toggle === 'function') {
        window.OdditCartDrawer.toggle();
      }

      this._syncCartAriaExpanded();
    }

    _syncCartAriaExpanded() {
      if (!this.cartToggle) return;
      var drawer = document.querySelector('.oddit-cart-drawer');
      var isOpen = drawer ? drawer.classList.contains('is-open') : false;
      this.cartToggle.setAttribute('aria-expanded', String(isOpen));
    }

    _updateCartCountBubble(cart) {
      var bubble = this.header.querySelector('[data-cart-count-bubble]');
      if (!bubble) return;
      var count = cart ? cart.item_count : 0;
      bubble.textContent = count;
      bubble.classList.toggle('oddit-header__cart-count--hidden', count === 0);
    }

    _observeCartDrawer() {
      if (this._cartDrawerObserver) {
        this._cartDrawerObserver.disconnect();
      }
      var drawer = document.querySelector('.oddit-cart-drawer');
      if (!drawer || !this.cartToggle) return;
      this._cartDrawerObserver = new MutationObserver(() => {
        this._syncCartAriaExpanded();
      });
      this._cartDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    _closeAllPanels() {
      // Close all desktop mega menu panels
      this.megaMenuPanels.forEach((panel) => {
        panel.classList.remove(CLASSES.menuOpen);
        panel.setAttribute('aria-hidden', 'true');
      });

      // Close mobile menu panel
      if (this.mobileMenuPanel) {
        this.mobileMenuPanel.classList.remove(CLASSES.menuOpen);
        this.mobileMenuPanel.setAttribute('aria-hidden', 'true');
      }
    }

    destroy() {
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('resize', this._onResize);
      if (this._onCartUpdated) {
        document.removeEventListener('cart:updated', this._onCartUpdated);
      }
      clearTimeout(this._resizeTimer);
      clearTimeout(this._hoverCloseTimer);
      if (this.megaMenuTriggers && this.megaMenuTriggers.length > 0) {
        this.megaMenuTriggers.forEach((trigger) => {
          trigger.removeEventListener('mouseenter', this._onTriggerMouseEnter);
          trigger.removeEventListener('mouseleave', this._onTriggerMouseLeave);
        });
        this.megaMenuPanels.forEach((panel) => {
          panel.removeEventListener('mouseenter', this._onPanelMouseEnter);
          panel.removeEventListener('mouseleave', this._onPanelMouseLeave);
        });
        this.header.removeEventListener('mouseleave', this._onHeaderMouseLeave);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('mouseenter', this._onOverlayMouseEnter);
      }
      if (this._cartDrawerObserver) {
        this._cartDrawerObserver.disconnect();
        this._cartDrawerObserver = null;
      }

      if (this.isOpen) {
        this.closeAll();
      }
    }
  }

  // Store instance references for cleanup
  const instances = new Map();

  function getSectionId(el) {
    if (!el) return null;
    if (el.dataset && el.dataset.sectionId) return el.dataset.sectionId;
    if (el.id && el.id.startsWith('shopify-section-')) return el.id.slice('shopify-section-'.length);
    return null;
  }

  function initSection(sectionEl) {
    var header = sectionEl ? sectionEl.querySelector(SELECTORS.header) : null;
    var target = header || sectionEl;
    if (!target) return;
    var sectionId = getSectionId(sectionEl);
    if (sectionId && instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }
    var instance = new OdditHeader(target);
    if (sectionId) {
      instances.set(sectionId, instance);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSection(document.querySelector('.oddit-header-section'));
    });
  } else {
    initSection(document.querySelector('.oddit-header-section'));
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector(SELECTORS.header)) {
      initSection(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail ? event.detail.sectionId : null;
    if (sectionId && instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }
  });
})();
